/**
 * Pure helpers for the Collections page: the builder tree's selection,
 * resolving collection objects against the taxonomy, and the list search.
 *
 * Everything is keyed by node `uuid`: the tree (from `buildObjectTree`), the
 * picked selection, and a collection object's `key`. A taxonomy node's own `key`
 * is its slug and is never read here (see "Object identity" in
 * `@utils/api/collections`).
 *
 * Selection: every object has its own model, so each detectable node
 * (`is_available`) is picked on its own — picking a parent never picks its
 * children. A node without a model can't be picked.
 */

const isModelBacked = node => Boolean(node?.is_available);

/**
 * The builder's pickable tree. Prunes nodes that aren't detectable and have no
 * detectable descendants (dead-end branches are dropped rather than shown), and
 * marks the remaining nodes without a model `disabled`: they stay only to give
 * their detectable descendants a place in the tree.
 */
export const filterAvailableNodes = (nodes = []) =>
	nodes.reduce((result, node) => {
		const children = filterAvailableNodes(node.children ?? []);
		if (isModelBacked(node) || children.length > 0) {
			result.push({ ...node, children, disabled: !isModelBacked(node) });
		}
		return result;
	}, []);

export const objectCountLabel = count => `${count} ${count === 1 ? 'object' : 'objects'}`;

/**
 * Toggle a node's own uuid in the picked list, keeping the existing order. A
 * node without a model can't be picked, so it leaves the list as it is.
 */
export const toggleNode = (node, pickedNodeUuids = []) => {
	if (!isModelBacked(node)) return pickedNodeUuids;
	return pickedNodeUuids.includes(node.uuid)
		? pickedNodeUuids.filter(uuid => uuid !== node.uuid)
		: [...pickedNodeUuids, node.uuid];
};

/**
 * Index the tree by node uuid: each node with the names of its ancestors, so
 * a row can show "Tree › Conifer" beneath "Spruce".
 *
 * @returns {Map<string, { node: object, path: string[] }>}
 */
export const indexByUuid = (nodes = []) => {
	const index = new Map();
	const walk = (node, path) => {
		index.set(node.uuid, { node, path });
		(node.children ?? []).forEach(child => walk(child, [...path, node.name]));
	};
	nodes.forEach(node => walk(node, []));
	return index;
};

/**
 * A collection's objects with their colour and ancestor path from the taxonomy.
 * The label is the collection's own name for the object, falling back to the
 * taxonomy's for a newly picked one. Objects missing from the taxonomy keep
 * their own label, with an empty path; `color` may stay undefined for the
 * caller to fill.
 */
export const resolveObjects = (objects = [], index) =>
	objects.map(object => {
		const entry = index.get(object.key);
		return {
			key: object.key,
			label: object.label || entry?.node.name || object.key,
			// TODO: saving a new collection stores this taxonomy colour too, so later
			// taxonomy colour changes never reach it. Save only colours the user picked.
			color: object.color ?? entry?.node.color,
			path: entry?.path ?? [],
		};
	});

/**
 * Whether an object can be sent in a new collection, as a template copy does: the
 * API refuses a node outside this tab's taxonomy with 422.
 */
export const isSendable = (object, index) => index.has(object.key);

/**
 * A collection matches when its name or any of its objects' labels contains
 * the query (case-insensitive). `query` is expected trimmed and lowercased.
 */
export const matchesCollection = (collection, query) =>
	!query ||
	Boolean(collection.name?.toLowerCase().includes(query)) ||
	Boolean(collection.objects?.some(object => object.label?.toLowerCase().includes(query)));
